/* Hợp đồng lao động, thâm niên, văn bản, CKTM, dữ liệu đoàn: các hàm nghiệp vụ nhỏ. Port từ app cũ. */
import { today as todayFn } from './dates';

/* ---------- hợp đồng ---------- */
export type ContractKind = 'hdld' | 'pl' | 'tv' | 'ck';
export const CK_KINDS: Record<ContractKind, [string, string]> = {
  hdld: ['Hợp đồng lao động', 'HĐLĐ'],
  pl: ['Phụ lục HĐLĐ', 'PLHĐ'],
  tv: ['Hợp đồng thử việc', 'HĐTV'],
  ck: ['Bản cam kết', 'CK'],
};

export interface ContractLite {
  id?: string;
  kind: ContractKind;
  empId?: string;
  no?: string;
  signDate?: string;
  fromDate?: string;
  toDate?: string;
}

export type PillTone = 'ok' | 'wait' | 'off' | 'mat' | 'pause';
export type StatusPill = [label: string, tone: PillTone];

/** Hiệu lực hợp đồng; "sắp hết hạn" là trong vòng 30 ngày. */
export function conStatus(c: Pick<ContractLite, 'kind' | 'toDate'>, todayIso: string = todayFn()): StatusPill {
  if (!c.toDate) return c.kind === 'ck' ? ['Đã ký', 'ok'] : ['Còn hiệu lực', 'ok'];
  if (c.toDate < todayIso) return ['Hết hạn', 'off'];
  const lim = new Date(todayIso + 'T00:00:00'); lim.setDate(lim.getDate() + 30);
  const limIso = `${lim.getFullYear()}-${String(lim.getMonth() + 1).padStart(2, '0')}-${String(lim.getDate()).padStart(2, '0')}`;
  return c.toDate <= limIso ? ['Sắp hết hạn', 'wait'] : ['Còn hiệu lực', 'ok'];
}

/** Hợp đồng hiện hành của nhân viên: HĐLĐ/HĐTV có hiệu lực từ ngày gần nhất không vượt quá hôm nay. */
export function currentContract<C extends ContractLite>(contracts: C[], empId: string, todayIso: string = todayFn()): C | undefined {
  return contracts
    .filter(c => c.empId === empId && ['hdld', 'tv'].includes(c.kind) && (c.fromDate || '') <= todayIso)
    .sort((a, b) => (b.fromDate || '').localeCompare(a.fromDate || ''))[0];
}

/** Số hợp đồng kế tiếp: {HĐLĐ}-{năm}-{001}. */
export function nextContractNo(contracts: ContractLite[], kind: ContractKind, date: string | undefined, todayIso: string = todayFn()): string {
  const y = (date || todayIso).slice(0, 4), pre = `${CK_KINDS[kind][1]}-${y}-`;
  let max = 0;
  for (const c of contracts.filter(x => x.kind === kind)) {
    if ((c.no || '').startsWith(pre)) { const n = parseInt((c.no as string).slice(pre.length), 10); if (n > max) max = n; }
  }
  return pre + String(max + 1).padStart(3, '0');
}

/** Ngày hết hạn tự tính = ngày hiệu lực + số tháng − 1 ngày. */
export function contractEnd(fromIso: string, months: number): string {
  const d = new Date(fromIso + 'T00:00:00');
  d.setMonth(d.getMonth() + months);
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Thâm niên dạng "x năm, y tháng, z ngày". */
export function seniority(e: { startDate?: string; endDate?: string }, todayIso: string = todayFn()): string {
  if (!e.startDate) return '';
  const a = new Date(e.startDate + 'T00:00:00'), b = new Date((e.endDate || todayIso) + 'T00:00:00');
  let y = b.getFullYear() - a.getFullYear(), m = b.getMonth() - a.getMonth(), d = b.getDate() - a.getDate();
  if (d < 0) { m--; d += new Date(b.getFullYear(), b.getMonth(), 0).getDate(); }
  if (m < 0) { y--; m += 12; }
  return y < 0 ? '' : `${y} năm, ${m} tháng, ${d} ngày`;
}

/* ---------- văn bản ---------- */
export interface DocLite {
  type: string;
  no?: string;
  date?: string;
  inNo?: string;
  inDate?: string;
  due?: string;
  handleStatus?: string;
  status?: string;
  effTo?: string;
}

export const VB_TYPES: Record<string, { name: string; kh?: string; dir: 'in' | 'out'; eff?: 1; emp?: 1 }> = {
  tb: { name: 'Thông báo', kh: 'TB', dir: 'out' },
  qd: { name: 'Quyết định', kh: 'QĐ', dir: 'out', eff: 1, emp: 1 },
  cvdi: { name: 'Công văn đi', kh: 'CV', dir: 'out' },
  cvden: { name: 'Công văn đến', dir: 'in' },
  ttr: { name: 'Tờ trình', kh: 'TTr', dir: 'out' },
  bb: { name: 'Biên bản', kh: 'BB', dir: 'out', emp: 1 },
  qc: { name: 'Quy chế – Quy định', kh: 'QC', dir: 'out', eff: 1 },
  khac: { name: 'Văn bản khác', kh: 'VB', dir: 'out' },
};

export const vbYear = (d: Pick<DocLite, 'type' | 'inDate' | 'date'>): string => (d.type === 'cvden' ? d.inDate : d.date || '')?.slice(0, 4) || '';

export function vbNextNo(docs: DocLite[], type: string, date: string | undefined, org = 'FAS', todayIso: string = todayFn()): string {
  const y = (date || todayIso).slice(0, 4);
  const same = docs.filter(d => d.type === type && vbYear(d) === y);
  const seq = same.reduce((m, d) => Math.max(m, parseInt((type === 'cvden' ? d.inNo : d.no) || '', 10) || 0), 0) + 1;
  return type === 'cvden' ? String(seq) : `${seq}/${VB_TYPES[type].kh}-${org}`;
}

export function vbState(d: DocLite, todayIso: string = todayFn()): StatusPill {
  if (d.type === 'cvden') {
    if (d.handleStatus === 'Đã xử lý') return ['Đã xử lý', 'ok'];
    if (d.due && d.due < todayIso) return ['Quá hạn xử lý', 'off'];
    return [d.handleStatus || 'Chưa xử lý', 'wait'];
  }
  if (d.status === 'Dự thảo') return ['Dự thảo', 'pause'];
  if (d.status === 'Thu hồi / hết hiệu lực' || (d.effTo && d.effTo < todayIso)) return ['Hết hiệu lực', 'off'];
  return ['Đã ban hành', 'ok'];
}

/* ---------- CKTM ---------- */
export interface CktmLite { amount?: number | string | null; base?: number | string | null; rate?: number | string | null }
export const ckAmt = (r: CktmLite): number =>
  r.amount !== '' && r.amount !== undefined && r.amount !== null ? Number(r.amount) || 0 : Math.round(((Number(r.base) || 0) * (Number(r.rate) || 0)) / 100);
export const ckPer = (p?: string): string => (p ? p.slice(5, 7) + '/' + p.slice(0, 4) : '');

/* ---------- dữ liệu đoàn ---------- */
export type GroupKind = 'Cọc' | 'Hoàn cọc' | 'Phạt cọc';

export const slug = (x: unknown): string => String(x ?? '').trim().replace(/[^A-Za-z0-9_\-.~:@+]+/g, '-').replace(/^-+|-+$/g, '');

/** Khóa tự nhiên của dòng dữ liệu đoàn: số vé + kiểu + số receipt (hoặc stt). */
export function rowKey(r: { tkt?: unknown; kind?: string; receipt?: unknown; stt?: unknown }, fallbackSeed: string | number = Date.now()): string {
  const kindCode = ({ 'Cọc': 'COC', 'Hoàn cọc': 'HOAN', 'Phạt cọc': 'PHAT' } as Record<string, string>)[r.kind || ''] || slug(r.kind);
  return slug(`${r.tkt}_${kindCode}_${r.receipt || r.stt}`).slice(0, 190) || 'row-' + fallbackSeed;
}

export function normKind(k: unknown): string {
  const x = String(k || '').trim().toLowerCase();
  if (x.startsWith('hoàn') || x.startsWith('hoan')) return 'Hoàn cọc';
  if (x.startsWith('phạt') || x.startsWith('phat')) return 'Phạt cọc';
  if (x.startsWith('cọc') || x.startsWith('coc') || x === 'deposit') return 'Cọc';
  return String(k || '').trim();
}
